import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MdAdd,
  MdArchive,
  MdBookmarkBorder,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdCloudUpload,
  MdContentCopy,
  MdDeleteOutline,
  MdPhotoCamera,
  MdPrint,
  MdSettings,
  MdSwapHoriz,
  MdUnarchive,
} from 'react-icons/md';
import admissionsAPI from '../../api/admissionsAPI';
import TransferStudentModal from './TransferStudentModal';
import OdooAutocomplete from './OdooAutocomplete';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { fetchWithHrToken } from '../../utils/hrApiHelpers';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';
import '../../employeeAttendance-admissions.css';

const PARENT_TYPES = [
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'brother', label: 'Brother' },
  { value: 'sister', label: 'Sister' },
  { value: 'other', label: 'Other' },
];

const BLOOD_GROUPS = ['A+ve', 'A-ve', 'B+ve', 'B-ve', 'AB+ve', 'AB-ve', 'O+ve', 'O-ve'];
const BLOOD_ODOO_TO_UI = {
  'A+': 'A+ve',
  'A-': 'A-ve',
  'B+': 'B+ve',
  'B-': 'B-ve',
  'AB+': 'AB+ve',
  'AB-': 'AB-ve',
  'O+': 'O+ve',
  'O-': 'O-ve',
};
const BLOOD_UI_TO_ODOO = Object.fromEntries(
  Object.entries(BLOOD_ODOO_TO_UI).map(([odoo, ui]) => [ui, odoo])
);

const CAMPUS_FALLBACK = [
  { value: 'prof_addow', label: 'Prof Addow Campus' },
  { value: 'prof_shahiid', label: 'Prof Shahiid Campus' },
  { value: 'prof_shurie', label: 'Prof Shurie Campus' },
];

const STATES = [
  { value: 'draft', label: 'Registration' },
  { value: 'fee_paid', label: 'Admission Fee Paid' },
  { value: 'admitted', label: 'Admitted' },
  { value: 'detain', label: 'Detain' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STUDY_TYPES = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'Evening', label: 'Evening' },
  { value: 'weekEnd', label: 'Week End' },
];

function odooId(value) {
  if (value == null || value === false) return '';
  if (Array.isArray(value)) return String(value[0] ?? '');
  if (typeof value === 'object' && value.id != null) return String(value.id);
  return String(value);
}

function odooText(value) {
  if (value == null || value === false) return '';
  if (Array.isArray(value)) return String(value[1] ?? value[0] ?? '');
  if (typeof value === 'object' && value.name != null) return String(value.name);
  return String(value);
}

function firstText(...values) {
  for (const v of values) {
    const t = odooText(v);
    if (t) return t;
  }
  return '';
}

function normalizeBlood(value) {
  const raw = firstText(value);
  if (!raw) return '';
  if (BLOOD_GROUPS.includes(raw)) return raw;
  return BLOOD_ODOO_TO_UI[raw] || raw;
}

function Field({ label, children, required, help }) {
  return (
    <div className="ea-odoo-field">
      <label className="ea-odoo-field__label">
        {label}
        {required ? <span className="ea-adm-req">*</span> : null}
        {help ? (
          <span className="ea-odoo-help" title={help}>
            i
          </span>
        ) : null}
      </label>
      <div className="ea-odoo-field__value">{children}</div>
    </div>
  );
}

function RadioRow({ name, value, onChange, options, required }) {
  return (
    <div className="ea-adm-radios" role="radiogroup" aria-required={required || undefined}>
      {options.map((opt, idx) => (
        <label key={opt.value} className="ea-adm-radio">
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            required={required && idx === 0 && !value}
          />
          <span className="ea-adm-radio__label">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="ea-odoo-group">
      <h3 className="ea-odoo-group__title">{title}</h3>
      <div className="ea-odoo-group__body">{children}</div>
    </div>
  );
}

export default function UgStudentFormPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    mother_name: '',
    gender: '',
    birth_date: '',
    birth_place: '',
    nationality_id: '',
    phone: '',
    email: '',
    blood_group: '',
    hemis_number: '',
    school_id: '',
    roll_no: '',
    certificate_number: '',
    graduation_year: '',
    average: '',
    study_type: '',
    admission_intake_id: '',
    district: '',
    campus: '',
    faculty_id: '',
    department_id: '',
    batch_id: '',
    category: 'undergraduate',
    parent_type: '',
    parent_phone: '',
    parent_email: '',
    student_no: '',
    image: null,
  });
  const [meta, setMeta] = useState({
    name: '',
    state: 'admitted',
    active: true,
    faculty_name: '',
    department_name: '',
    batch_name: '',
    image_url: null,
  });
  const [campuses, setCampuses] = useState(CAMPUS_FALLBACK);
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [schools, setSchools] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const actionsRef = useRef(null);
  const isArchived = meta.active === false;

  useEffect(() => {
    if (!actionsOpen) {
      setPrintOpen(false);
      return undefined;
    }
    const onDoc = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setActionsOpen(false);
        setPrintOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [actionsOpen]);

  const setField = (key, value) => {
    setDirty(true);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const fullName = useMemo(
    () => [form.first_name, form.middle_name, form.last_name].filter(Boolean).join(' '),
    [form.first_name, form.middle_name, form.last_name]
  );
  const titleName = fullName || meta.name || 'Student';

  const photoSrc =
    photoPreview ||
    (meta.image_url && String(meta.image_url).startsWith('data:')
      ? meta.image_url
      : null);

  const mapStudent = useCallback((s) => {
    const faculty_id = odooId(s.faculty_id_int || s.faculty_id || s.faculty);
    const department_id = odooId(s.department_id_int || s.department_id || s.department);
    const batch_id = odooId(s.batch_id_int || s.batch_id || s.batch);
    const school_id = odooId(s.school_id_int || s.school_id || s.school);
    const nationality_id = odooId(s.nationality_id_int || s.nationality_id || s.nationality);
    const admission_intake_id = odooId(
      s.admission_intake_id_int || s.admission_intake_id || s.admission_intake
    );
    const genderRaw = firstText(s.gender).toLowerCase();
    const parentTypeRaw = firstText(s.parent_type).toLowerCase();
    setForm({
      first_name: firstText(s.first_name),
      middle_name: firstText(s.middle_name),
      last_name: firstText(s.last_name),
      mother_name: firstText(s.mother_name, s.parent_name),
      gender: genderRaw === 'female' || genderRaw === 'f' ? 'female' : genderRaw === 'male' || genderRaw === 'm' ? 'male' : '',
      birth_date: firstText(s.birth_date).slice(0, 10),
      birth_place: firstText(s.birth_place),
      nationality_id,
      phone: firstText(s.phone),
      email: firstText(s.email),
      blood_group: normalizeBlood(s.blood_group_label || s.blood_group),
      hemis_number: firstText(s.hemis_number),
      school_id,
      roll_no: firstText(s.roll_no),
      certificate_number: firstText(s.certificate_number),
      graduation_year: firstText(s.graduation_year).slice(0, 10),
      average: s.average != null && s.average !== false ? String(s.average) : '',
      study_type: firstText(s.study_type),
      admission_intake_id,
      district: firstText(s.district),
      campus: firstText(s.campus),
      faculty_id,
      department_id,
      batch_id,
      category: 'undergraduate',
      parent_type: PARENT_TYPES.some((p) => p.value === parentTypeRaw) ? parentTypeRaw : '',
      parent_phone: firstText(s.parent_phone, s.mobile),
      parent_email: firstText(s.parent_email),
      student_no: firstText(s.student_no),
      image: null,
    });
    setMeta({
      name: firstText(s.name),
      state: firstText(s.state) || 'admitted',
      active: !(
        s.active === false ||
        s.active === 0 ||
        s.active === '0' ||
        s.active === 'false'
      ),
      faculty_name: firstText(s.faculty_name, s.faculty),
      department_name: firstText(s.department_name, s.department),
      batch_name: firstText(s.batch_name, s.batch),
      image_url:
        s.image_url && String(s.image_url).startsWith('data:')
          ? s.image_url
          : s.image
            ? `data:image/jpeg;base64,${s.image}`
            : null,
    });
    if (nationality_id) {
      setNationalities((prev) =>
        prev.some((n) => String(n.id) === String(nationality_id))
          ? prev
          : [...prev, { id: Number(nationality_id), name: firstText(s.nationality_name, s.nationality) || `#${nationality_id}` }]
      );
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const lookups = await Promise.all([
        fetchWithHrToken(ensureHrToken, (t) => admissionsAPI.getCampuses(t)),
        fetchWithHrToken(ensureHrToken, (t) => admissionsAPI.getFaculties(t, { exclude_pg: true })),
        fetchWithHrToken(ensureHrToken, (t) => admissionsAPI.getSchools('', t, { limit: 500 })),
        fetchWithHrToken(ensureHrToken, (t) => admissionsAPI.getIntakes(t)),
        fetchWithHrToken(ensureHrToken, (t) => admissionsAPI.getNationalities('', t, { limit: 500 })),
        fetchWithHrToken(ensureHrToken, (t) => admissionsAPI.getDistricts(t)),
      ]);
      if (cancelled) return;
      const [campusRes, facRes, schoolRes, intakeRes, natRes, distRes] = lookups;
      const campusList = campusRes.data?.campuses || campusRes.data?.records || [];
      if (campusList.length) setCampuses(campusList);
      setFaculties(facRes.data?.faculties || facRes.data?.records || []);
      setSchools(schoolRes.data?.schools || schoolRes.data?.records || []);
      setIntakes(intakeRes.data?.intakes || intakeRes.data?.records || []);
      setNationalities(natRes.data?.records || natRes.data?.nationalities || []);
      setDistricts(distRes.data?.districts || distRes.data?.records || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureHrToken]);

  useEffect(() => {
    if (!form.faculty_id) {
      setDepartments([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data } = await fetchWithHrToken(ensureHrToken, (t) =>
        admissionsAPI.getDepartments(form.faculty_id, t, { exclude_pg: true })
      );
      if (!cancelled) setDepartments(data?.departments || data?.records || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureHrToken, form.faculty_id]);

  useEffect(() => {
    if (!form.department_id && !form.faculty_id) {
      setBatches([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data } = await fetchWithHrToken(ensureHrToken, (t) =>
        admissionsAPI.getBatches(form.department_id || undefined, t, {
          faculty_id: form.faculty_id || undefined,
          exclude_pg: true,
        })
      );
      if (!cancelled) setBatches(data?.batches || data?.records || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureHrToken, form.department_id, form.faculty_id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (t) =>
        admissionsAPI.getStudent({ student_id: Number(studentId), include_image: true }, t)
      );
      if (cancelled) return;
      setLoading(false);
      if (fetchError) {
        setError(fetchError);
        return;
      }
      mapStudent(data?.student || data);
      setDirty(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureHrToken, studentId, mapStudent]);

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      setPhotoPreview(result);
      const b64 = result.includes(',') ? result.split(',')[1] : result;
      setField('image', b64);
    };
    reader.readAsDataURL(file);
  };

  const onSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setError(null);
    const payload = {
      first_name: form.first_name,
      middle_name: form.middle_name,
      last_name: form.last_name,
      mother_name: form.mother_name,
      gender: form.gender,
      birth_date: form.birth_date || null,
      birth_place: form.birth_place,
      nationality_id: form.nationality_id ? Number(form.nationality_id) : null,
      phone: form.phone,
      email: form.email,
      blood_group: BLOOD_UI_TO_ODOO[form.blood_group] || form.blood_group || null,
      hemis_number: form.hemis_number,
      school_id: form.school_id ? Number(form.school_id) : null,
      roll_no: form.roll_no,
      certificate_number: form.certificate_number,
      graduation_year: form.graduation_year || null,
      average: form.average,
      study_type: form.study_type || null,
      admission_intake_id: form.admission_intake_id ? Number(form.admission_intake_id) : null,
      district: form.district || null,
      campus: form.campus || null,
      faculty_id: form.faculty_id ? Number(form.faculty_id) : null,
      department_id: form.department_id ? Number(form.department_id) : null,
      batch_id: form.batch_id ? Number(form.batch_id) : null,
      parent_type: form.parent_type || null,
      mobile: form.parent_phone,
      parent_email: form.parent_email,
      student_no: form.student_no || null,
    };
    if (form.image) payload.image = form.image;
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (t) =>
      admissionsAPI.updateStudent({ student_id: Number(studentId), ...payload }, t)
    );
    setSaving(false);
    if (fetchError) {
      setError(fetchError);
      return;
    }
    if (data?.student) mapStudent(data.student);
    setDirty(false);
  };

  const closeActions = () => {
    setActionsOpen(false);
    setPrintOpen(false);
  };

  const onPrintIdCard = async () => {
    if (actionBusy) return;
    closeActions();
    setActionBusy(true);
    setError(null);
    setActionMessage(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.printStudentIdCard(Number(studentId), token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      return;
    }
    const b64 = data?.pdf_base64;
    if (!b64) {
      setError('Student ID Card PDF was empty.');
      return;
    }
    try {
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: data?.mimetype || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        const a = document.createElement('a');
        a.href = url;
        a.download = data?.filename || 'Student-ID-Card.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setActionMessage('Student ID Card opened. Use the browser print dialog if needed.');
    } catch (err) {
      setError(err?.message || 'Could not open Student ID Card PDF.');
    }
  };

  const onDuplicate = async () => {
    if (actionBusy) return;
    closeActions();
    setActionBusy(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.duplicateStudent(Number(studentId), token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      return;
    }
    const newId = data?.student?.id;
    if (newId) {
      navigate(`/bu-dashboard/admissions/ug-student-list/${newId}`, { replace: true });
    }
  };

  const onToggleArchive = () => {
    if (actionBusy) return;
    closeActions();
    setArchiveOpen(true);
  };

  const onArchiveConfirm = async () => {
    if (actionBusy) return;
    setActionBusy(true);
    setError(null);
    const nextActive = isArchived;
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.archiveStudent(Number(studentId), nextActive, token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      setArchiveOpen(false);
      return;
    }
    setArchiveOpen(false);
    const student = data?.student;
    const nowActive =
      student && typeof student.active !== 'undefined'
        ? !(student.active === false || student.active === 0 || student.active === 'false')
        : nextActive;
    if (student) mapStudent({ ...student, active: nowActive });
    else setMeta((prev) => ({ ...prev, active: nowActive }));
    setActionMessage(nowActive ? 'Student restored (unarchived).' : 'Student archived.');
  };

  const openDeleteModal = () => {
    if (actionBusy) return;
    closeActions();
    setDeleteOpen(true);
  };

  const onDeleteConfirm = async () => {
    if (actionBusy) return;
    setActionBusy(true);
    setError(null);
    const { error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.deleteStudent(Number(studentId), token)
    );
    setActionBusy(false);
    if (fetchError) {
      setError(fetchError);
      setDeleteOpen(false);
      return;
    }
    setDeleteOpen(false);
    navigate('/bu-dashboard/admissions/ug-student-list', { replace: true });
  };

  const onTransferStudent = () => {
    if (actionBusy) return;
    closeActions();
    setTransferOpen(true);
  };

  const onTransferSuccess = (data) => {
    if (data?.student) mapStudent(data.student);
    setActionMessage(
      data?.message ||
        (data?.new_student_no
          ? `Student transferred successfully. New Student ID: ${data.new_student_no}`
          : 'Student transferred successfully.')
    );
  };

  const onDiscard = () => window.location.reload();

  if (loading) {
    return (
      <div className="ea-odoo-form-page ea-adm-form ea-ug-form">
        <div className="ea-odoo-empty">Loading student…</div>
      </div>
    );
  }

  return (
    <div className="ea-odoo-form-page ea-adm-form ea-ug-form">
      <div className="ea-odoo-form-bar ea-adm-cp">
        <div className="ea-adm-cp__left">
          <button
            type="button"
            className="ea-odoo-btn ea-adm-cp__new"
            onClick={() => navigate('/bu-dashboard/admissions/ug-student-list')}
          >
            New
          </button>
          <div className="ea-adm-cp__title-block">
            <Link
              to="/bu-dashboard/admissions/ug-student-list"
              className="ea-adm-cp__parent"
            >
              UG student list report
            </Link>
            <div className="ea-adm-cp__record-row">
              <span className="ea-adm-cp__record">{titleName}</span>
              <div className="ea-adm-actions" ref={actionsRef}>
                <button
                  type="button"
                  className="ea-adm-cp__icon"
                  title="Actions"
                  aria-label="Actions"
                  aria-expanded={actionsOpen}
                  aria-haspopup="menu"
                  onClick={() => setActionsOpen((v) => !v)}
                  disabled={actionBusy}
                >
                  <MdSettings size={16} />
                </button>
                {actionsOpen ? (
                  <div className="ea-adm-actions__menu ea-adm-actions__menu--wide" role="menu">
                    <div
                      className={`ea-adm-actions__item ea-adm-actions__item--has-sub${
                        printOpen ? ' is-open' : ''
                      }`}
                      role="none"
                      onMouseEnter={() => setPrintOpen(true)}
                      onFocus={() => setPrintOpen(true)}
                    >
                      <button
                        type="button"
                        className="ea-adm-actions__item-btn"
                        role="menuitem"
                        aria-haspopup="menu"
                        aria-expanded={printOpen}
                        onClick={() => setPrintOpen((v) => !v)}
                        disabled={actionBusy}
                      >
                        <span className="ea-adm-actions__item-main">
                          <MdPrint size={16} aria-hidden />
                          Print
                        </span>
                        <MdChevronRight size={16} aria-hidden />
                      </button>
                      {printOpen ? (
                        <div className="ea-adm-actions__submenu" role="menu">
                          <button
                            type="button"
                            className="ea-adm-actions__item"
                            role="menuitem"
                            onClick={onPrintIdCard}
                            disabled={actionBusy}
                          >
                            Student ID Card
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="ea-adm-actions__item"
                      role="menuitem"
                      onClick={onDuplicate}
                      disabled={actionBusy}
                    >
                      <MdContentCopy size={16} aria-hidden />
                      Duplicate
                    </button>
                    <button
                      type="button"
                      className="ea-adm-actions__item"
                      role="menuitem"
                      onClick={onToggleArchive}
                      disabled={actionBusy}
                    >
                      {isArchived ? (
                        <MdUnarchive size={16} aria-hidden />
                      ) : (
                        <MdArchive size={16} aria-hidden />
                      )}
                      {isArchived ? 'Unarchive' : 'Archive'}
                    </button>
                    <button
                      type="button"
                      className="ea-adm-actions__item ea-adm-actions__item--danger"
                      role="menuitem"
                      onClick={openDeleteModal}
                      disabled={actionBusy}
                    >
                      <MdDeleteOutline size={16} aria-hidden />
                      Delete
                    </button>
                    <div className="ea-adm-actions__sep" role="separator" />
                    <button
                      type="button"
                      className="ea-adm-actions__item"
                      role="menuitem"
                      onClick={onTransferStudent}
                    >
                      <MdSwapHoriz size={16} aria-hidden />
                      Transfer Student
                    </button>
                  </div>
                ) : null}
              </div>
              {dirty ? (
                <>
                  <button
                    type="button"
                    className="ea-adm-cp__icon ea-adm-cp__icon--save"
                    onClick={onSave}
                    disabled={saving || actionBusy}
                    title="Save"
                    aria-label="Save"
                  >
                    <MdCloudUpload size={18} />
                  </button>
                  <button
                    type="button"
                    className="ea-adm-cp__icon"
                    onClick={onDiscard}
                    disabled={saving || actionBusy}
                    title="Discard"
                    aria-label="Discard"
                  >
                    <MdClose size={18} />
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
        <div className="ea-adm-cp__right">
          <button
            type="button"
            className="ea-adm-cp__icon"
            title="Favorite"
            aria-label="Favorite"
          >
            <MdBookmarkBorder size={18} />
          </button>
          <div className="ea-odoo-pager ea-adm-cp__pager" aria-label="Pagination">
            <span>1 / 1</span>
            <div className="ea-adm-cp__pager-btns">
              <button
                type="button"
                className="ea-odoo-pager__btn"
                onClick={() => navigate('/bu-dashboard/admissions/ug-student-list')}
                aria-label="Previous"
              >
                <MdChevronLeft size={18} />
              </button>
              <button type="button" className="ea-odoo-pager__btn" disabled aria-label="Next">
                <MdChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {error ? (
        <div className="ea-odoo-alert ea-odoo-alert--error" role="alert">
          {error}
        </div>
      ) : null}
      {actionMessage ? (
        <div className="ea-odoo-alert ea-odoo-alert--info" role="status">
          {actionMessage}
        </div>
      ) : null}

      <form className="ea-odoo-sheet" onSubmit={onSave}>
        <div className="ea-ug-form__statusbar" role="status" aria-label="Student status">
          {STATES.map((st) => (
            <span
              key={st.value}
              className={`ea-ug-form__status${meta.state === st.value ? ' is-active' : ''}`}
            >
              {st.label}
            </span>
          ))}
        </div>

        <header className="ea-ug-form__header">
          <button
            type="button"
            className="ea-odoo-sheet__photo"
            onClick={() => fileInputRef.current?.click()}
            aria-label={photoSrc ? 'Change photo' : 'Add photo'}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="ea-odoo-sheet__photo-input"
              onChange={handleImagePick}
              tabIndex={-1}
            />
            {photoSrc ? (
              <img src={photoSrc} alt="" />
            ) : (
              <span className="ea-odoo-sheet__photo-placeholder">
                <MdAdd size={28} />
              </span>
            )}
            <span className="ea-odoo-sheet__photo-overlay">
              <MdPhotoCamera size={22} />
              <span>{photoSrc ? 'Edit' : 'Upload'}</span>
            </span>
          </button>

          <div className="ea-ug-form__identity">
            <div className="ea-ug-form__student-id">
              Student ID :{' '}
              <input
                className="ea-ug-form__id-input"
                value={form.student_no}
                onChange={(e) => setField('student_no', e.target.value)}
              />
            </div>
            <OdooAutocomplete
              className="ea-odoo-ac--title ea-odoo-ac--faculty"
              value={form.faculty_id}
              options={faculties}
              aria-label="Faculty"
              onChange={(id) => {
                setField('faculty_id', id);
                setField('department_id', '');
                setField('batch_id', '');
              }}
            />
            <OdooAutocomplete
              className="ea-odoo-ac--title ea-odoo-ac--dept"
              value={form.department_id}
              options={departments}
              aria-label="Department"
              onChange={(id) => {
                setField('department_id', id);
                setField('batch_id', '');
              }}
            />
            <OdooAutocomplete
              className="ea-odoo-ac--title ea-odoo-ac--batch"
              value={form.batch_id}
              options={batches}
              aria-label="Batch"
              onChange={(id) => setField('batch_id', id)}
            />
          </div>
        </header>

        <div className="ea-odoo-sheet__body">
          <div className="ea-odoo-cols">
            <Group title="Personl Information">
              <Field label="Full Name">
                <input value={fullName} readOnly />
              </Field>
              <Field label="First Name" required>
                <input
                  value={form.first_name}
                  onChange={(e) => setField('first_name', e.target.value)}
                  required
                />
              </Field>
              <Field label="Middle Name" required>
                <input
                  value={form.middle_name}
                  onChange={(e) => setField('middle_name', e.target.value)}
                  required
                />
              </Field>
              <Field label="Last Name" required>
                <input
                  value={form.last_name}
                  onChange={(e) => setField('last_name', e.target.value)}
                  required
                />
              </Field>
              <Field label="Mother Name">
                <input
                  value={form.mother_name}
                  onChange={(e) => setField('mother_name', e.target.value)}
                />
              </Field>
              <Field label="Gender" required>
                <RadioRow
                  name="ug-gender"
                  value={form.gender}
                  onChange={(v) => setField('gender', v)}
                  required
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                  ]}
                />
              </Field>
              <Field label="Date of birth" required>
                <input
                  type="date"
                  value={form.birth_date}
                  onChange={(e) => setField('birth_date', e.target.value)}
                  required
                />
              </Field>
              <Field label="Birth Place">
                <input
                  value={form.birth_place}
                  onChange={(e) => setField('birth_place', e.target.value)}
                />
              </Field>
              <Field label="Nationality" required>
                <OdooAutocomplete
                  value={form.nationality_id}
                  options={nationalities}
                  onChange={(id) => setField('nationality_id', id)}
                  required
                  aria-label="Nationality"
                  emptyText="No countries match"
                />
              </Field>
            </Group>

            <Group title="Contact Details">
              <Field label="Student Phone Number" required>
                <input
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  required
                />
              </Field>
              <Field label="Student Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                />
              </Field>
              <Field label="Blood Group">
                <OdooAutocomplete
                  value={form.blood_group}
                  options={BLOOD_GROUPS}
                  onChange={(id) => setField('blood_group', id)}
                  aria-label="Blood Group"
                />
              </Field>
              <Field label="Hemis Number">
                <input
                  value={form.hemis_number}
                  onChange={(e) => setField('hemis_number', e.target.value)}
                />
              </Field>
            </Group>
          </div>

          <section className="ea-odoo-group ea-odoo-group--wide">
            <h3 className="ea-odoo-group__title">Education Information</h3>
            <div className="ea-odoo-cols">
              <div className="ea-odoo-group__body">
                <Field label="School Name" required>
                  <OdooAutocomplete
                    value={form.school_id}
                    options={schools}
                    onChange={(id) => setField('school_id', id)}
                    required
                    aria-label="School Name"
                  />
                </Field>
                <Field label="Roll Name School">
                  <input
                    value={form.roll_no}
                    onChange={(e) => setField('roll_no', e.target.value)}
                  />
                </Field>
                <Field label="Certificate Number" required>
                  <input
                    value={form.certificate_number}
                    onChange={(e) => setField('certificate_number', e.target.value)}
                    required
                  />
                </Field>
                <Field label="Year of graduation">
                  <input
                    type="date"
                    value={form.graduation_year}
                    onChange={(e) => setField('graduation_year', e.target.value)}
                  />
                </Field>
                <Field label="Average">
                  <input
                    value={form.average}
                    onChange={(e) => setField('average', e.target.value)}
                  />
                </Field>
              </div>
              <div className="ea-odoo-group__body">
                <Field label="Student Shift">
                  <OdooAutocomplete
                    value={form.study_type}
                    options={STUDY_TYPES}
                    onChange={(id) => setField('study_type', id)}
                    aria-label="Student Shift"
                  />
                </Field>
                <Field label="Admission Intake">
                  <OdooAutocomplete
                    value={form.admission_intake_id}
                    options={intakes}
                    onChange={(id) => setField('admission_intake_id', id)}
                    aria-label="Admission Intake"
                  />
                </Field>
                <Field label="District">
                  <OdooAutocomplete
                    value={form.district}
                    options={districts}
                    onChange={(id) => setField('district', id)}
                    aria-label="District"
                  />
                </Field>
                <Field label="Campus" required>
                  <OdooAutocomplete
                    value={form.campus}
                    options={campuses}
                    onChange={(id) => setField('campus', id)}
                    required
                    aria-label="Campus"
                  />
                </Field>
                <Field label="Batch" required>
                  <OdooAutocomplete
                    value={form.batch_id}
                    options={batches}
                    onChange={(id) => setField('batch_id', id)}
                    required
                    aria-label="Batch"
                  />
                </Field>
                <Field label="Student Category" help="Undergraduate">
                  <RadioRow
                    name="ug-category"
                    value={form.category}
                    onChange={(v) => setField('category', v)}
                    options={[{ value: 'undergraduate', label: 'Undergraduate' }]}
                  />
                </Field>
              </div>
            </div>
          </section>
        </div>

        <div className="ea-odoo-tabs">
          <button type="button" className="ea-odoo-tabs__btn active">
            Family Information
          </button>
        </div>
        <div className="ea-odoo-sheet__body ea-adm-tab-body">
          <Field label="Parent Type">
            <RadioRow
              name="ug-parent-type"
              value={form.parent_type}
              onChange={(v) => setField('parent_type', v)}
              options={PARENT_TYPES}
            />
          </Field>
          <Field label="Parent Name">
            <input
              value={form.mother_name}
              onChange={(e) => setField('mother_name', e.target.value)}
            />
          </Field>
          <Field label="Parent Phone Number">
            <input
              value={form.parent_phone}
              onChange={(e) => setField('parent_phone', e.target.value)}
            />
          </Field>
          <Field label="Parent Email">
            <input
              type="email"
              value={form.parent_email}
              onChange={(e) => setField('parent_email', e.target.value)}
            />
          </Field>
        </div>
      </form>

      {deleteOpen ? (
        <div
          className="ea-odoo-modal-backdrop"
          role="presentation"
          onClick={() => !actionBusy && setDeleteOpen(false)}
        >
          <div
            className="ea-odoo-modal ea-odoo-modal--delete"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ea-ug-student-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ea-odoo-modal__header">
              <h2 id="ea-ug-student-delete-title">Bye-bye, record!</h2>
              <button
                type="button"
                className="ea-odoo-modal__close"
                aria-label="Close"
                disabled={actionBusy}
                onClick={() => setDeleteOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="ea-odoo-modal__body">
              <p>Ready to make your record disappear into thin air? Are you sure?</p>
              <p>It will be gone forever!</p>
              <p>Think twice before you click that &apos;Delete&apos; button!</p>
            </div>
            <div className="ea-odoo-modal__footer">
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--danger"
                disabled={actionBusy}
                onClick={onDeleteConfirm}
              >
                {actionBusy ? 'Deleting…' : 'Delete'}
              </button>
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--ghost"
                disabled={actionBusy}
                onClick={() => setDeleteOpen(false)}
              >
                No, keep it
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {archiveOpen ? (
        <div
          className="ea-odoo-modal-backdrop"
          role="presentation"
          onClick={() => !actionBusy && setArchiveOpen(false)}
        >
          <div
            className="ea-odoo-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ea-ug-student-archive-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ea-odoo-modal__header">
              <h2 id="ea-ug-student-archive-title">Confirmation</h2>
              <button
                type="button"
                className="ea-odoo-modal__close"
                aria-label="Close"
                disabled={actionBusy}
                onClick={() => setArchiveOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="ea-odoo-modal__body">
              <p>
                {isArchived
                  ? 'Are you sure that you want to unarchive this record?'
                  : 'Are you sure that you want to archive this record?'}
              </p>
            </div>
            <div className="ea-odoo-modal__footer">
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--danger"
                disabled={actionBusy}
                onClick={onArchiveConfirm}
              >
                {actionBusy
                  ? isArchived
                    ? 'Unarchiving…'
                    : 'Archiving…'
                  : isArchived
                    ? 'Unarchive'
                    : 'Archive'}
              </button>
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--ghost"
                disabled={actionBusy}
                onClick={() => setArchiveOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <TransferStudentModal
        open={transferOpen}
        studentId={studentId}
        ensureHrToken={ensureHrToken}
        onClose={() => setTransferOpen(false)}
        onSuccess={onTransferSuccess}
      />
    </div>
  );
}
